import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const ConfirmEmail = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking | success | error
  const [message, setMessage] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [confirmationType, setConfirmationType] = useState('signup');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const { hash, search, origin, pathname } = window.location;
    const hashParams = new URLSearchParams(hash?.replace(/^#/, ''));
    const queryParams = new URLSearchParams(search);

    const urlError = queryParams?.get('error_description') || hashParams?.get('error_description');
    if (urlError) {
      setMessage(decodeURIComponent(urlError));
      setStatus('error');
      return;
    }

    const type = queryParams?.get('type') || hashParams?.get('type');
    const allowedTypes = ['signup', 'invite', 'email_change'];
      setConfirmationType(type || 'signup');

    if (type && !allowedTypes.includes(type)) {
      setMessage('El enlace de verificación no es válido o ya expiró. Solicita un nuevo correo de confirmación.');
      setStatus('error');
      return;
    }

    const confirmEmail = async () => {
      try {
        let exchangeError = null;

        if (queryParams?.get('code')) {
          const code = queryParams?.get('code');
          const { error } = await supabase?.auth?.exchangeCodeForSession(code);
          exchangeError = error;
        } else if (hashParams?.get('access_token') && hashParams?.get('refresh_token')) {
          const { error } = await supabase?.auth?.setSession({
            access_token: hashParams?.get('access_token'),
            refresh_token: hashParams?.get('refresh_token')
          });
          exchangeError = error;
        } else if (hashParams?.get('token_hash')) {
          const token_hash = hashParams?.get('token_hash');
          const verificationType = hashParams?.get('type') || 'signup';
          const { error } = await supabase?.auth?.verifyOtp({
            type: verificationType,
            token_hash
          });
          exchangeError = error;
        } else {
          exchangeError = {
            message: 'El enlace que abriste está incompleto. Vuelve a abrir el correo desde el mismo dispositivo.'
          };
        }

        if (exchangeError) {
          setMessage(exchangeError?.message || 'No pudimos confirmar tu correo. Solicita un nuevo enlace de verificación.');
          setStatus('error');
          return;
        }

        const { data: userData, error: userError } = await supabase?.auth?.getUser();
        if (!userError && userData?.user?.email) {
          setUserEmail(userData?.user?.email);
        }

        window.history.replaceState({}, '', `${origin}${pathname}`);
        setStatus('success');
      } catch (error) {
        setMessage('No pudimos validar tu enlace. Intenta nuevamente desde el correo o solicita uno nuevo.');
        setStatus('error');
      }
    };

    confirmEmail();
  }, []);

  const handleInvitePassword = async (event) => {
    event?.preventDefault();
    setPasswordError('');

    if (password.length < 8) {
      setPasswordError(
        'La contraseña debe tener al menos 8 caracteres.'
      );
      return;
    }

    if (
      !/[A-Z]/.test(password) ||
      !/[a-z]/.test(password) ||
      !/[0-9]/.test(password)
    ) {
      setPasswordError(
        'La contraseña debe contener mayúscula, minúscula y número.'
      );
      return;
    }

    if (password !== confirmPassword) {
      setPasswordError(
        'Las contraseñas no coinciden.'
      );
      return;
    }

    setSavingPassword(true);

    try {
      const { error } =
        await supabase.auth.updateUser({
          password
        });

      if (error) {
        setPasswordError(
          error.message ||
          'No fue posible guardar la contraseña.'
        );
        return;
      }

      await supabase.auth.signOut();

      setPassword('');
      setConfirmPassword('');
      setPasswordSaved(true);
    } catch (error) {
      setPasswordError(
        'No fue posible guardar la contraseña. Intenta nuevamente.'
      );
    } finally {
      setSavingPassword(false);
    }
  };


  const renderIcon = () => {
    if (status === 'checking') {
      return (
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <Icon name="Loader2" size={28} className="animate-spin text-primary" />
        </div>
      );
    }

    if (status === 'success') {
      return (
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
          <Icon name="CheckCircle" size={28} className="text-success" />
        </div>
      );
    }

    return (
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-error/10">
        <Icon name="AlertTriangle" size={28} className="text-error" />
      </div>
    );
  };

  const renderContent = () => {
    if (status === 'checking') {
      return (
        <div className="space-y-4 text-center">
          <p className="text-lg font-semibold text-text-primary">Verificando tu correo…</p>
          <p className="text-sm text-text-secondary">
            Estamos validando la información del enlace. Este proceso solo tomará unos segundos.
          </p>
        </div>
      );
    }

    if (status === 'success') {
      if (confirmationType === 'invite') {
        if (passwordSaved) {
          return (
            <div className="space-y-6 text-center">
              <div className="space-y-2">
                <h1 className="text-2xl font-semibold text-text-primary">
                  ¡Cuenta activada!
                </h1>

                <p className="text-sm text-text-secondary">
                  Tu correo fue confirmado y tu contraseña quedó creada correctamente.
                  Ya puedes iniciar sesión en Mercado Bulk.
                </p>
              </div>

              <Button
                onClick={() => navigate('/login')}
                variant="default"
              >
                Ir al inicio de sesión
              </Button>
            </div>
          );
        }

        return (
          <div className="space-y-6">
            <div className="space-y-2 text-center">
              <h1 className="text-2xl font-semibold text-text-primary">
                Activa tu cuenta
              </h1>

              <p className="text-sm text-text-secondary">
                {userEmail
                  ? `El correo ${userEmail} fue confirmado. Ahora crea tu contraseña para terminar de activar tu cuenta.`
                  : 'Tu correo fue confirmado. Ahora crea tu contraseña para terminar de activar tu cuenta.'}
              </p>
            </div>

            <form
              onSubmit={handleInvitePassword}
              className="space-y-4 text-left"
            >
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1.5">
                  Contraseña
                </label>

                <input
                  type="password"
                  value={password}
                  onChange={(event) =>
                    setPassword(event.target.value)
                  }
                  autoComplete="new-password"
                  disabled={savingPassword}
                  placeholder="Crea tu contraseña"
                  className="w-full rounded-xl border border-border bg-background px-4 py-3 text-text-primary outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-1.5">
                  Confirmar contraseña
                </label>

                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(event) =>
                    setConfirmPassword(event.target.value)
                  }
                  autoComplete="new-password"
                  disabled={savingPassword}
                  placeholder="Repite tu contraseña"
                  className="w-full rounded-xl border border-border bg-background px-4 py-3 text-text-primary outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>

              <p className="text-xs text-text-secondary">
                Mínimo 8 caracteres, con mayúscula, minúscula y número.
              </p>

              {passwordError && (
                <div className="rounded-lg border border-error/20 bg-error/10 p-3 text-sm text-error">
                  {passwordError}
                </div>
              )}

              <button
                type="submit"
                disabled={savingPassword}
                className="w-full rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-white transition-smooth hover:bg-primary/90 disabled:opacity-60"
              >
                {savingPassword
                  ? 'Guardando contraseña...'
                  : 'Crear contraseña y activar cuenta'}
              </button>
            </form>
          </div>
        );
      }

      return (
        <div className="space-y-6 text-center">
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-text-primary">
              ¡Correo verificado!
            </h1>

            <p className="text-sm text-text-secondary">
              {userEmail
                ? `La cuenta ${userEmail} se confirmó correctamente. Ya puedes iniciar sesión y comenzar a usar la plataforma.`
                : 'Tu cuenta se confirmó correctamente. Ya puedes iniciar sesión y comenzar a usar la plataforma.'}
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Button
              onClick={() => navigate('/login')}
              variant="default"
            >
              Ir al inicio de sesión
            </Button>

            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
            >
              Ir al panel
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6 text-center">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-text-primary">Enlace inválido o expirado</h1>
          <p className="text-sm text-text-secondary">
            {message || 'Solicita un nuevo correo de verificación desde la pantalla de registro.'}
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button onClick={() => navigate('/login')} variant="default">
            Ir al inicio de sesión
          </Button>
          <Button onClick={() => navigate('/login')} variant="ghost">
            Ir al inicio de sesión
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>Confirmación de correo - MercadoLibre Bulk Publisher</title>
      </Helmet>
      <div
        className="relative min-h-screen flex items-center justify-center overflow-hidden px-4 sm:px-6 lg:px-8"
        style={{
          backgroundImage: "url('/assets/images/login-background.avif')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-transparent dark:from-black/70 dark:via-black/60 dark:to-black/70 mix-blend-normal pointer-events-none" aria-hidden="true" />
        <div className="relative w-full max-w-lg">
          <div className="rounded-3xl border border-white/40 dark:border-white/10 bg-white/95 dark:bg-[#0f172ae6] shadow-elevated shadow-2xl p-10 space-y-6 transition-smooth">
            <div className="flex flex-col items-center gap-6">
              {renderIcon()}
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConfirmEmail;
